import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Minus, Plus, X, ArrowRight, Gift } from "lucide-react";
import { useCart } from "../context/CartContext";
import { createCheckout } from "../lib/api";
import { PayPalCheckout } from "./PayPalCheckout";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export const CartDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQty, subtotal, count } = useCart();
  const [loading, setLoading] = useState(false);
  const [giftWrap, setGiftWrap] = useState(false);
  const [giftMessage, setGiftMessage] = useState("");
  const [giftRecipientEmail, setGiftRecipientEmail] = useState("");
  const [giftRecipientName, setGiftRecipientName] = useState("");
  const navigate = useNavigate();
  const hasEbook = items.some((i) => i.lookup_key === "lg_ebook");

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout(items, window.location.origin, {
        giftWrap, giftMessage, giftRecipientEmail, giftRecipientName,
      });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error("Unable to start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-cream border-l border-line p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-6 border-b border-line">
          <SheetTitle className="font-display text-2xl text-ink flex items-center justify-between">
            Your Cart
            <span className="font-body text-sm text-ink/50">{count} item{count !== 1 ? "s" : ""}</span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-4">
            <p className="font-editorial text-3xl text-emerald">Your cart is empty.</p>
            <p className="font-body text-ink/60 text-sm">Discover the framework in print, ebook, and collector’s editions.</p>
            <button className="btn-outline mt-4" onClick={() => setIsOpen(false)} data-testid="cart-continue-shopping">
              Continue browsing
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 divide-y divide-line">
              {items.map((item) => (
                <div key={item.lookup_key} className="py-5 flex gap-4" data-testid={`cart-item-${item.id}`}>
                  <div className="w-16 h-20 bg-surface border border-line flex items-center justify-center shrink-0 overflow-hidden">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="font-display text-gold text-xl">LG</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <h4 className="font-display text-base text-ink leading-tight">{item.name}</h4>
                      <button onClick={() => removeItem(item.lookup_key)} aria-label="Remove" data-testid={`cart-remove-${item.id}`}>
                        <X className="w-4 h-4 text-ink/40 hover:text-ink" />
                      </button>
                    </div>
                    <p className="font-body text-xs text-ink/50 mt-0.5">{item.format}</p>
                    {item.personalization && (
                      <p className="font-body text-xs text-emerald mt-1 italic" data-testid={`cart-dedication-${item.id}`}>
                        Dedicated to: {item.personalization}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-line">
                        <button className="p-1.5 hover:bg-surface" onClick={() => updateQty(item.lookup_key, item.quantity - 1)} data-testid={`cart-dec-${item.id}`}>
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="px-3 font-body text-sm">{item.quantity}</span>
                        <button className="p-1.5 hover:bg-surface" onClick={() => updateQty(item.lookup_key, item.quantity + 1)} data-testid={`cart-inc-${item.id}`}>
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="font-body text-sm text-emerald font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-line px-6 py-5 bg-cream">
              <button
                onClick={() => setGiftWrap((v) => !v)}
                className={`w-full flex items-center gap-3 p-3 border transition-colors ${giftWrap ? "border-gold bg-gold/10" : "border-line hover:border-gold/50"}`}
                data-testid="gift-wrap-toggle"
              >
                <span className={`w-5 h-5 border flex items-center justify-center shrink-0 ${giftWrap ? "bg-gold border-gold" : "border-line"}`}>
                  {giftWrap && <span className="w-2 h-2 bg-ink" />}
                </span>
                <Gift className="w-4 h-4 text-emerald" />
                <span className="font-body text-sm text-ink flex-1 text-left">Make it a gift</span>
                <span className="font-body text-sm text-emerald font-semibold">+$5.00</span>
              </button>
              {giftWrap && (
                <textarea
                  value={giftMessage}
                  onChange={(e) => setGiftMessage(e.target.value)}
                  maxLength={300}
                  rows={2}
                  placeholder="Add a short gift message (optional)"
                  data-testid="gift-message-input"
                  className="w-full mt-3 bg-surface border border-line focus:border-emerald outline-none p-3 font-body text-sm text-ink resize-none transition-colors"
                />
              )}
              {giftWrap && hasEbook && (
                <div className="mt-3 border border-gold/40 bg-gold/5 p-3" data-testid="gift-recipient-block">
                  <p className="font-body text-xs text-emerald mb-2 font-medium">
                    Send the ebook straight to the recipient
                  </p>
                  <input
                    type="text"
                    value={giftRecipientName}
                    onChange={(e) => setGiftRecipientName(e.target.value)}
                    placeholder="Recipient's name"
                    data-testid="gift-recipient-name"
                    className="w-full mb-2 bg-cream border border-line focus:border-emerald outline-none p-2.5 font-body text-sm text-ink transition-colors"
                  />
                  <input
                    type="email"
                    value={giftRecipientEmail}
                    onChange={(e) => setGiftRecipientEmail(e.target.value)}
                    placeholder="Recipient's email"
                    data-testid="gift-recipient-email"
                    className="w-full bg-cream border border-line focus:border-emerald outline-none p-2.5 font-body text-sm text-ink transition-colors"
                  />
                  <p className="font-body text-[11px] text-ink/45 mt-2">
                    We’ll email them the ebook and your message the moment your order is confirmed.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-line px-6 py-6 space-y-4 bg-surface">
              <div className="flex justify-between font-body">
                <span className="text-ink/60">Subtotal{giftWrap ? " + gift wrap" : ""}</span>
                <span className="text-ink font-semibold" data-testid="cart-subtotal">${(subtotal + (giftWrap ? 5 : 0)).toFixed(2)}</span>
              </div>
              <p className="text-xs text-ink/45 font-body">Taxes & shipping calculated securely at checkout.</p>
              <button
                onClick={checkout}
                disabled={loading}
                className="btn-emerald w-full justify-center disabled:opacity-60"
                data-testid="cart-checkout-btn"
              >
                {loading ? "Redirecting…" : <>Secure Checkout <ArrowRight className="w-4 h-4" /></>}
              </button>

              <div className="flex items-center gap-3 my-2">
                <span className="flex-1 h-px bg-line" />
                <span className="text-[11px] text-ink/40 font-body uppercase tracking-widest">or</span>
                <span className="flex-1 h-px bg-line" />
              </div>

              <div data-testid="paypal-slot">
                <PayPalCheckout
                  items={items}
                  gift={{ giftWrap, giftMessage, giftRecipientEmail, giftRecipientName }}
                  disabled={loading || items.length === 0}
                  onApproved={(orderId) => {
                    setIsOpen(false);
                    navigate(`/payment/success?session_id=${orderId}`);
                  }}
                />
              </div>

              <p className="text-center text-[11px] text-ink/40 font-body">Powered by Stripe &amp; PayPal</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
